import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { CartAddedModal } from "@/components/CartAddedModal";
import { CnhCheckDialog } from "@/components/CnhCheckDialog";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ExitIntentModal } from "@/components/ExitIntentModal";
import { PromotionBanner } from "@/components/PromotionBanner";
import { CookieConsent } from "@/components/CookieConsent";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { installAnalytics } from "@/lib/analytics";
import Index from "./pages/Index";
import { AdminLayout } from "@/layouts/AdminLayout";
import NotFound from "@/pages/NotFound";

// ── Code-split: marketing pages ──────────────────────────────────────────
const SouContador = lazy(() => import("./pages/SouContador"));
const Suporte = lazy(() => import("./pages/Suporte"));
const Produtos = lazy(() => import("./pages/Produtos"));
const CertificadoPF = lazy(() => import("./pages/CertificadoPF"));
const CertificadoPJ = lazy(() => import("./pages/CertificadoPJ"));
const CertificadoMEI = lazy(() => import("./pages/CertificadoMEI"));
const Assinaturas = lazy(() => import("./pages/Assinaturas"));
const CertificateWizard = lazy(() => import("./pages/CertificateWizard"));
const ContatoSuporte = lazy(() => import("./pages/ContatoSuporte"));
const DriversInstaladores = lazy(() => import("./pages/DriversInstaladores"));
const Manuais = lazy(() => import("./pages/Manuais"));
const EmitirCertificado = lazy(() => import("./pages/EmitirCertificado"));
const TestarCertificado = lazy(() => import("./pages/TestarCertificado"));
const SejaParceiro = lazy(() => import("./pages/SejaParceiro"));
const MeusPedidos = lazy(() => import("./pages/MeusPedidos"));

// ── Code-split: store + checkout ─────────────────────────────────────────
const CheckoutPage = lazy(() => import("@/pages/store/CheckoutPage"));
const OrderTrackingPage = lazy(() => import("@/pages/store/OrderTrackingPage"));
const ProductPage = lazy(() => import("@/pages/store/ProductPage"));

// ── Code-split: customer area ────────────────────────────────────────────
const CustomerLoginPage = lazy(() => import("@/pages/cliente/LoginPage"));
const MinhaContaPage = lazy(() => import("@/pages/cliente/MinhaContaPage"));

// ── Code-split: soluções Biz (verticals) ────────────────────────────────
const BizValida = lazy(() => import("@/pages/solucoes/BizValida"));
const BizIA = lazy(() => import("@/pages/solucoes/BizIA"));
const BizFinance = lazy(() => import("@/pages/solucoes/BizFinance"));
const BizImpulso = lazy(() => import("@/pages/solucoes/BizImpulso"));
const Privacidade = lazy(() => import("@/pages/Privacidade"));
const Termos = lazy(() => import("@/pages/Termos"));
const ExcluirDados = lazy(() => import("@/pages/ExcluirDados"));
const PublicacoesApps = lazy(() => import("@/pages/PublicacoesApps"));
const DiggerNewEra = lazy(() => import("@/pages/apps/DiggerNewEra"));
const DiggerNewEraPrivacidade = lazy(() => import("@/pages/apps/DiggerNewEraPrivacidade"));

// ── Code-split: blog ─────────────────────────────────────────────────────
const BlogIndex = lazy(() => import("@/pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/blog/BlogPost"));

// ── Code-split: landing pages ────────────────────────────────────────────
const CertificadoDigitalHub = lazy(() => import("@/pages/landing/CertificadoDigitalHub"));
const CertificadoDigitalEcpf = lazy(() => import("@/pages/landing/CertificadoDigitalEcpf"));
const CertificadoDigitalEcnpj = lazy(() => import("@/pages/landing/CertificadoDigitalEcnpj"));
const CertificadoDigitalA1vsA3 = lazy(() => import("@/pages/landing/CertificadoDigitalA1vsA3"));
const RenovarCertificadoDigital = lazy(() => import("@/pages/landing/RenovarCertificadoDigital"));
const CertificadoDigitalNFe = lazy(() => import("@/pages/landing/CertificadoDigitalNFe"));
const CertificadoDigitalEsocial = lazy(() => import("@/pages/landing/CertificadoDigitalEsocial"));
const CertificadoDigitalContador = lazy(() => import("@/pages/landing/CertificadoDigitalContador"));
const CertificadoDigitalMigrarSerasa = lazy(() => import("@/pages/landing/CertificadoDigitalMigrarSerasa"));
const CertificadoDigitalAdvogado = lazy(() => import("@/pages/landing/CertificadoDigitalAdvogado"));
const CertificadoDigitalMedico = lazy(() => import("@/pages/landing/CertificadoDigitalMedico"));
const CertificadoDigitalEngenheiro = lazy(() => import("@/pages/landing/CertificadoDigitalEngenheiro"));
const CertificadoDigitalEnfermeiro = lazy(() => import("@/pages/landing/CertificadoDigitalEnfermeiro"));
const CertificadoDigitalDentista = lazy(() => import("@/pages/landing/CertificadoDigitalDentista"));
const CertificadoDigitalContabilidade = lazy(() => import("@/pages/landing/CertificadoDigitalContabilidade"));
const CertificadoDigitalPsicologo = lazy(() => import("@/pages/landing/CertificadoDigitalPsicologo"));
const CertificadoDigitalAdministrador = lazy(() => import("@/pages/landing/CertificadoDigitalAdministrador"));
const CertificadoDigitalNutricionista = lazy(() => import("@/pages/landing/CertificadoDigitalNutricionista"));
const CertificadoDigitalCentroSP = lazy(() => import("@/pages/landing/CertificadoDigitalCentroSP"));
const CertificadoDigitalPaulistaSP = lazy(() => import("@/pages/landing/CertificadoDigitalPaulistaSP"));
const CertificadoDigitalZonaSulSP = lazy(() => import("@/pages/landing/CertificadoDigitalZonaSulSP"));
const ProfissoesHub = lazy(() => import("@/pages/landing/ProfissoesHub"));

// ── Code-split: admin (separate vendor chunk via Vite manualChunks) ──────
const LoginPage = lazy(() => import("@/pages/admin/LoginPage"));
const OverviewPage = lazy(() => import("@/pages/admin/OverviewPage"));
const OrdersPage = lazy(() => import("@/pages/admin/OrdersPage"));
const OrderDetailPage = lazy(() => import("@/pages/admin/OrderDetailPage"));
const ProductsPage = lazy(() => import("@/pages/admin/ProductsPage"));
const CustomersPage = lazy(() => import("@/pages/admin/CustomersPage"));
const CustomerDetailPage = lazy(() => import("@/pages/admin/CustomerDetailPage"));
const AnalyticsPage = lazy(() => import("@/pages/admin/AnalyticsPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));
const TwoFactorPage = lazy(() => import("@/pages/admin/TwoFactorPage"));
const AuditPage = lazy(() => import("@/pages/admin/AuditPage"));
const BlogAdminPage = lazy(() => import("@/pages/admin/BlogPage"));
const BlogAutomationPage = lazy(() => import("@/pages/admin/BlogAutomationPage"));
const ReelsAutomationPage = lazy(() => import("@/pages/admin/ReelsAutomationPage"));
const SocialPage = lazy(() => import("@/pages/admin/SocialPage"));
const WhatsAppPage = lazy(() => import("@/pages/admin/WhatsAppPage"));
const PromotionsPage = lazy(() => import("@/pages/admin/PromotionsPage"));
const CouponsPage = lazy(() => import("@/pages/admin/CouponsPage"));
const LeadsPage = lazy(() => import("@/pages/admin/LeadsPage"));
const OperationPage = lazy(() => import("@/pages/admin/OperationPage"));

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    // Install first-party analytics tracker on first mount. Silently no-ops
    // on the server (SSR) and in admin routes (see component-level opt-out).
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin")) {
      installAnalytics();
    }
  }, []);

  return (
    <ErrorBoundary>
      <Toaster />
      <BrowserRouter>
        <PromotionBanner />
        <CartAddedModal />
        <CnhCheckDialog />
        <WhatsAppButton />
        <ExitIntentModal />
        <CookieConsent />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/sou-contador" element={<SouContador />} />
            <Route path="/suporte" element={<Suporte />} />
            <Route path="/produtos" element={<Produtos />} />
            <Route path="/certificado-pf" element={<CertificadoPF />} />
            <Route path="/certificado-pj" element={<CertificadoPJ />} />
            <Route path="/certificado-mei" element={<CertificadoMEI />} />
            <Route path="/assinaturas" element={<Assinaturas />} />
            <Route path="/qual-certificado" element={<CertificateWizard />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/meus-pedidos" element={<MeusPedidos />} />
            <Route path="/pedidos/:orderCode" element={<OrderTrackingPage />} />
            <Route path="/produto/:slug" element={<ProductPage />} />
            <Route path="/certificado-digital" element={<CertificadoDigitalHub />} />
            <Route path="/certificado-digital/ecpf" element={<CertificadoDigitalEcpf />} />
            <Route path="/certificado-digital/ecnpj" element={<CertificadoDigitalEcnpj />} />
            <Route path="/certificado-digital/a1-ou-a3" element={<CertificadoDigitalA1vsA3 />} />
            <Route path="/renovar-certificado-digital" element={<RenovarCertificadoDigital />} />
            <Route path="/certificado-digital/nfe" element={<CertificadoDigitalNFe />} />
            <Route path="/certificado-digital/esocial" element={<CertificadoDigitalEsocial />} />
            <Route path="/certificado-digital/contador" element={<CertificadoDigitalContador />} />
            <Route path="/certificado-digital/migrar-serasa" element={<CertificadoDigitalMigrarSerasa />} />
            <Route path="/certificado-digital/advogado" element={<CertificadoDigitalAdvogado />} />
            <Route path="/certificado-digital/medico" element={<CertificadoDigitalMedico />} />
            <Route path="/certificado-digital/engenheiro" element={<CertificadoDigitalEngenheiro />} />
            <Route path="/certificado-digital/enfermeiro" element={<CertificadoDigitalEnfermeiro />} />
            <Route path="/certificado-digital/dentista" element={<CertificadoDigitalDentista />} />
            <Route path="/certificado-digital/contabilidade" element={<CertificadoDigitalContabilidade />} />
            <Route path="/certificado-digital/psicologo" element={<CertificadoDigitalPsicologo />} />
            <Route path="/certificado-digital/administrador" element={<CertificadoDigitalAdministrador />} />
            <Route path="/certificado-digital/nutricionista" element={<CertificadoDigitalNutricionista />} />
            <Route path="/certificado-digital/centro-sp" element={<CertificadoDigitalCentroSP />} />
            <Route path="/certificado-digital/paulista-sp" element={<CertificadoDigitalPaulistaSP />} />
            <Route path="/certificado-digital/zona-sul-sp" element={<CertificadoDigitalZonaSulSP />} />
            <Route path="/profissoes" element={<ProfissoesHub />} />
            <Route path="/contato-e-suporte" element={<ContatoSuporte />} />
            <Route path="/privacidade" element={<Privacidade />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacidade" replace />} />
            <Route path="/politica-de-privacidade" element={<Navigate to="/privacidade" replace />} />
            <Route path="/termos-de-servico" element={<Termos />} />
            <Route path="/terms-of-service" element={<Navigate to="/termos-de-servico" replace />} />
            <Route path="/termos" element={<Navigate to="/termos-de-servico" replace />} />
            <Route path="/excluir-meus-dados" element={<ExcluirDados />} />
            <Route path="/data-deletion" element={<Navigate to="/excluir-meus-dados" replace />} />
            <Route path="/exclusao-de-dados" element={<Navigate to="/excluir-meus-dados" replace />} />
            <Route path="/publicacoes-apps" element={<PublicacoesApps />} />
            <Route path="/publicacoes-apps/digger-new-era" element={<DiggerNewEra />} />
            <Route path="/publicacoes-apps/digger-new-era/privacidade" element={<DiggerNewEraPrivacidade />} />
            <Route path="/apps" element={<Navigate to="/publicacoes-apps" replace />} />
            <Route path="/apps/digger-new-era/privacy" element={<Navigate to="/publicacoes-apps/digger-new-era/privacidade" replace />} />
            <Route path="/drivers-e-instaladores" element={<DriversInstaladores />} />
            <Route path="/manuais" element={<Manuais />} />
            <Route path="/emitir-certificado" element={<EmitirCertificado />} />
            <Route path="/testar-certificado" element={<TestarCertificado />} />
            <Route path="/seja-parceiro" element={<SejaParceiro />} />

            {/* Customer area (magic-link) */}
            <Route path="/cliente/login" element={<CustomerLoginPage />} />
            <Route path="/minha-conta" element={<MinhaContaPage />} />

            {/* Soluções Biz (verticais de produto) */}
            <Route path="/biz-valida" element={<BizValida />} />
            <Route path="/biz-ia" element={<BizIA />} />
            <Route path="/biz-finance" element={<BizFinance />} />
            <Route path="/biz-impulso" element={<BizImpulso />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/auth" element={<Navigate to="/admin/login" replace />} />
            <Route path="/profile" element={<Navigate to="/admin" replace />} />

            <Route path="/admin/login" element={<LoginPage />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<OverviewPage />} />
              <Route path="/admin/pedidos" element={<OrdersPage />} />
              <Route path="/admin/pedidos/:orderCode" element={<OrderDetailPage />} />
              <Route path="/admin/produtos" element={<ProductsPage />} />
              <Route path="/admin/produtos/novo" element={<ProductsPage />} />
              <Route path="/admin/clientes" element={<CustomersPage />} />
              <Route path="/admin/clientes/:id" element={<CustomerDetailPage />} />
              <Route path="/admin/analytics" element={<AnalyticsPage />} />
              <Route path="/admin/auditoria" element={<AuditPage />} />
              <Route path="/admin/blog" element={<BlogAdminPage />} />
              <Route path="/admin/blog/automacao" element={<BlogAutomationPage />} />
              <Route path="/admin/reels" element={<ReelsAutomationPage />} />
              <Route path="/admin/redes-sociais" element={<SocialPage />} />
              <Route path="/admin/promocoes" element={<PromotionsPage />} />
              <Route path="/admin/cupons" element={<CouponsPage />} />
              <Route path="/admin/leads" element={<LeadsPage />} />
              <Route path="/admin/operacao" element={<OperationPage />} />
              <Route path="/admin/whatsapp" element={<WhatsAppPage />} />
              <Route path="/admin/configuracoes" element={<SettingsPage />} />
              <Route path="/admin/configuracoes/2fa" element={<TwoFactorPage />} />
            </Route>

            <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
